import { useState, useEffect, useCallback, memo } from 'react';
import api from '../lib/api';
import { Crown, PartyPopper, X } from 'lucide-react';

/**
 * GameweekCelebration — transient Stage banner shown ONCE per new gameweek.
 * Celebrates the rollover ("Gameweek N has started") + shows the user's own
 * last-week result (rank / points / crown) stamped by week_advance.py.
 * Auto-dismisses after 15s; manual dismiss persists via localStorage.
 */

const CROWN_META = {
  gold: { label: 'League Champion', cls: 'text-yellow-400' },
  silver: { label: '2nd Place', cls: 'text-slate-300' },
  bronze: { label: '3rd Place', cls: 'text-amber-600' },
};

function lsKey(season, week) {
  return `gw_celebration_${season}_${week}`;
}

export default memo(function GameweekCelebration({ team }) {
  const [recap, setRecap] = useState(null);
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    api.get('/gameweek/recap').then((res) => {
      const r = res.data;
      if (!r || !r.last_week) return;
      try { if (localStorage.getItem(lsKey(r.season, r.current_week))) return; } catch { /* private mode */ }
      setRecap(r);
      setVisible(true);
    }).catch(() => {});
  }, []);

  const dismiss = useCallback(() => {
    setRecap((r) => {
      if (r) {
        try { localStorage.setItem(lsKey(r.season, r.current_week), String(Date.now())); } catch { /* private mode */ }
      }
      return r;
    });
    setLeaving(true);
    setTimeout(() => setVisible(false), 400);
  }, []);

  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(dismiss, 15000);
    return () => clearTimeout(t);
  }, [visible, dismiss]);

  if (!visible || !recap) return null;

  const results = recap.results || [];
  const mine = results.find((r) => r.team_id === team?.id)
    || [...results].sort((a, b) => (a.rank || 1e9) - (b.rank || 1e9))[0];
  const crown = mine?.crown ? CROWN_META[mine.crown] : null;

  return (
    <div
      className={`relative overflow-hidden rounded-xl border border-[hsl(var(--accent))]/40 bg-gradient-to-r from-[hsl(var(--accent))]/15 via-card to-[hsl(var(--accent))]/10 px-4 py-3 mb-3 transition-all duration-400 ${
        leaving ? 'opacity-0 -translate-y-2' : 'opacity-100 translate-y-0'
      }`}
      data-testid="gameweek-celebration"
    >
      <button
        onClick={dismiss}
        className="absolute top-2 right-2 text-muted-foreground hover:text-foreground transition-colors"
        aria-label="Dismiss"
        data-testid="gameweek-celebration-dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
      <div className="flex items-center gap-2 pr-6">
        {crown ? (
          <Crown className={`h-5 w-5 shrink-0 ${crown.cls}`} fill="currentColor" />
        ) : (
          <PartyPopper className="h-5 w-5 shrink-0 text-[hsl(var(--accent))]" />
        )}
        <span className="text-sm font-bold" data-testid="gameweek-celebration-title">
          Gameweek {recap.display_week || recap.current_week} has started!
        </span>
      </div>
      {mine && (
        <p className="text-[11px] text-muted-foreground mt-1.5 pr-6" data-testid="gameweek-celebration-result">
          {crown && (
            <span className={`font-semibold ${crown.cls}`}>{crown.label} · </span>
          )}
          Last week you finished{' '}
          <b className="text-foreground tabular-nums">#{mine.rank}</b>
          {mine.league_size ? <> of <b className="text-foreground tabular-nums">{mine.league_size}</b></> : null}
          {' '}in {mine.league_name || 'your league'} with{' '}
          <b className="text-foreground tabular-nums">{Number(mine.points || 0).toLocaleString()}</b> pts
        </p>
      )}
      {!mine && (
        <p className="text-[11px] text-muted-foreground mt-1.5 pr-6">
          A fresh week of scoring begins — weekly points and transfers have been reset. Good luck!
        </p>
      )}
    </div>
  );
});
